import React, { useEffect, useRef, useState } from 'react';
import { sanityClient } from '../../lib/sanity';
import FactoidRotator from './FactoidRotator';
import { FACTOID_INTERVAL_MS } from '../../config/timing';

/**
 * LoadingWithFactoids
 * - Shows existing spinner immediately
 * - After `delayMs`, and if feature flag enabled, shows FactoidRotator
 */
export default function LoadingWithFactoids({
  delayMs = 0,
  intervalMs = FACTOID_INTERVAL_MS,
  factoidsOverride = null,
  shouldSoftExit = false,
  onSoftExitComplete,
  hintText,
  showHint = true,
  progressMessages = null,
}) {
  const [factoids, setFactoids] = useState([]);
  const [exitArmed, setExitArmed] = useState(false);
  const manualExitRef = useRef(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (Array.isArray(factoidsOverride)) {
      setFactoids(factoidsOverride);
      return undefined;
    }

    const controller = new AbortController();
    const fetchFactoids = async () => {
      try {
        const query = `*[
          _type == "factoid" &&
          reviewStatus == "approved" &&
          defined(factoidV2.bucket)
        ]{
          title,
          expandedText,
          image,
          "slug": slug.current,
          "reviewStatus": coalesce(reviewStatus, "approved"),
          relatedContent->{ _type, slug, title }
        }`;
        const data = await sanityClient.fetch(query, {}, { signal: controller.signal });
        const shuffled = [...(Array.isArray(data) ? data : [])];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const selectedFactoids = shuffled.slice(0, 30);
        setFactoids(selectedFactoids);
      } catch (e) {
        if (e.name !== 'AbortError') {
          // Non-blocking: keep spinner if fetching fails
          console.warn('Failed to load factoids:', e.message);
        }
      }
    };

    fetchFactoids();
    return () => controller.abort();
  }, [factoidsOverride]);

  // Arm soft-exit when parent indicates loading has finished
  useEffect(() => {
    if (shouldSoftExit) setExitArmed(true);
  }, [shouldSoftExit]);

  useEffect(() => {
    if (exitArmed && factoids.length === 0 && !manualExitRef.current) {
      manualExitRef.current = true;
      if (typeof onSoftExitComplete === 'function') {
        onSoftExitComplete();
      }
    }
  }, [exitArmed, factoids.length, onSoftExitComplete]);

  // Determine the hint text to show
  const getHintText = () => {
    if (progressMessages && progressMessages.length > 0) {
      // Use progress messages if provided, cycling through them based on current index
      const index = Math.min(currentMessageIndex, progressMessages.length - 1);
      return progressMessages[index];
    }
    // Fall back to provided hintText or default
    return hintText || 'A Hawaii fact while we get things ready';
  };

  return (
    <div className="w-full flex justify-center p-0">
      <FactoidRotator
        factoids={factoids}
        intervalMs={intervalMs}
        limit={30}
        exitArmed={exitArmed}
        hintText={getHintText()}
        showHint={showHint}
        prominentHint={!!progressMessages}
        onFactoidChange={() => {
          // Update message index when factoid changes (cycle through all messages)
          if (progressMessages && progressMessages.length > 0) {
            setCurrentMessageIndex(prev => (prev + 1) % progressMessages.length);
          }
        }}
        onCycleEnd={() => {
          if (exitArmed) {
            setExitArmed(false);
            if (typeof onSoftExitComplete === 'function') onSoftExitComplete();
          }
        }}
      />
    </div>
  );
}
